import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, Send, Eye, X, Search, Trash2, User, UserPlus,
  Plus, FileText, DollarSign, Calendar, MessageSquare,
  Loader2, Mail, ChevronDown, Settings
} from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { cotizacionesApi, receptoresApi, productosApi, settingsApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import useEscClose from '../../hooks/useEscClose';
import InvoiceTemplate from '../../components/InvoiceTemplate';

// ── Toolbar button (same pattern as other pages) ──────────────────────
function ToolbarButton({ icon: Icon, label, onClick, disabled, active, variant = 'default' }) {
  const variants = {
    default: 'border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400',
    primary: 'border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500',
    warning: 'border border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500',
    success: 'border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500',
    danger: 'border border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500',
    orange: 'border border-orange-400 text-orange-600 hover:bg-orange-50 hover:border-orange-500',
  };
  const activeStyles = {
    warning: 'bg-amber-50 border-amber-500 border-2',
    orange: 'bg-orange-50 border-orange-500 border-2',
    default: 'bg-gray-100 border-gray-400',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`
        flex flex-col items-center justify-center
        px-1 py-1 rounded-lg transition-all font-medium toolbar-btn
        ${variants[variant]}
        ${active ? (activeStyles[variant] || activeStyles.default) : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <Icon className="toolbar-icon mb-0.5" />
      <span className="toolbar-label whitespace-nowrap">{label}</span>
    </button>
  );
}

// ── IVA options ───────────────────────────────────────────────────────
const IVA_OPTIONS = [
  { value: 0, label: 'Exento (0%)' },
  { value: 1, label: '1%' },
  { value: 2, label: '2%' },
  { value: 4, label: '4%' },
  { value: 8, label: '8%' },
  { value: 13, label: '13%' },
];

// ── Moneda options ────────────────────────────────────────────────────
const MONEDAS = [
  { value: 'CRC', label: 'Colones (CRC)', symbol: '\u20A1' },
  { value: 'USD', label: 'Dólares (USD)', symbol: '$' },
];

// ── Helper: new empty line ────────────────────────────────────────────
function crearLineaVacia(detalle = '') {
  return {
    id: Date.now() + Math.random(),
    cabys: '',
    codigo: '',
    detalle,
    cantidad: 1,
    precioUnitario: 0,
    descuento: 0,
    iva: 13,
  };
}

// ── Helper: calculate line totals ─────────────────────────────────────
function calcLinea(l) {
  const subtotal = l.cantidad * l.precioUnitario;
  const descMonto = subtotal * (l.descuento / 100);
  const base = subtotal - descMonto;
  const impuesto = base * (l.iva / 100);
  // Impuestos adicionales (911, Cruz Roja, etc.) sobre la base
  const impAdicional = (l.impuestosAdicionales || []).reduce(
    (s, imp) => s + base * ((parseFloat(imp.porcentaje) || 0) / 100), 0
  );
  const total = base + impuesto + impAdicional;
  return { subtotal, descMonto, base, impuesto, impAdicional, total };
}

// ── Format currency ───────────────────────────────────────────────────
function fmt(n, moneda) {
  const symbol = moneda === 'USD' ? '$' : '\u20A1';
  return `${symbol}${Number(n || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════
export default function CrearCotizacion() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const isEditing = !!paramId;

  const { currentEmisor, tipoCambio: storeTipoCambio, setPageStatus, clearPageStatus } = useAuthStore();

  // ── State ───────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Client
  const [receptor, setReceptor] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [searchingClient, setSearchingClient] = useState(false);

  // Product search
  const productInputRef = useRef(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Lines
  const [lineas, setLineas] = useState([]);
  const [selectedLineIds, setSelectedLineIds] = useState([]);

  // Settings
  const [codigoMoneda, setCodigoMoneda] = useState('USD');
  const [tipoCambio, setTipoCambio] = useState(storeTipoCambio || 1);
  const [fechaVencimiento, setFechaVencimiento] = useState(dayjs().add(15, 'day').format('YYYY-MM-DD'));
  const [referenciaOportunidad, setReferenciaOportunidad] = useState('');

  // Notes
  const [notas, setNotas] = useState('');
  const [terminos, setTerminos] = useState('');

  // Email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [cotizacionId, setCotizacionId] = useState(paramId || null);
  const [cotizacionNumero, setCotizacionNumero] = useState('');

  // Preview
  const [showPreview, setShowPreview] = useState(false);

  // Emisor
  const [emisorData, setEmisorData] = useState(null);

  // ── Page status ─────────────────────────────────────────────────────
  useEffect(() => {
    const monedaLabel = codigoMoneda === 'USD'
      ? `$ Dolares (TC: \u20A1${Number(tipoCambio).toFixed(2)})`
      : '\u20A1 Colones';
    setPageStatus({
      text: isEditing ? 'Editando Cotizacion...' : 'Nueva Cotizacion',
      subtext: monedaLabel,
      gradient: 'amber',
    });
    return () => clearPageStatus();
  }, [codigoMoneda, tipoCambio, isEditing, setPageStatus, clearPageStatus]);

  // ── Load emisor ─────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await settingsApi.getEmisor();
        if (res.data?.data) setEmisorData(res.data.data);
      } catch (e) {
        if (e?.code !== 'ERR_CANCELED') console.error('Error loading emisor:', e);
      }
    };
    load();
  }, [currentEmisor]);

  // ── Load cotizacion in edit mode ────────────────────────────────────
  useEffect(() => {
    if (!paramId) return;
    const cargar = async () => {
      try {
        setLoading(true);
        const res = await cotizacionesApi.getById(paramId);
        const data = res.data?.data;
        if (!data) { toast.error('Cotizacion no encontrada'); return; }

        setCotizacionId(data.id);
        setCotizacionNumero(data.numero || '');
        setCodigoMoneda(data.codigoMoneda || 'USD');
        setTipoCambio(data.tipoCambio || 1);
        setFechaVencimiento(data.fechaVencimiento ? dayjs(data.fechaVencimiento).format('YYYY-MM-DD') : '');
        setReferenciaOportunidad(data.referenciaOportunidad || '');
        setNotas(data.notas || '');
        setTerminos(data.terminos || '');

        if (data.receptorId) {
          try {
            const rr = await receptoresApi.getById(data.receptorId);
            const r = rr.data?.data;
            if (r) {
              setReceptor(r);
            }
          } catch {
            setReceptor({
              id: data.receptorId,
              nombre: data.clienteNombre || 'Cliente',
              email: data.clienteEmail || '',
            });
          }
        }

        const cargadas = (data.lineas || []).map((l, idx) => ({
          id: Date.now() + idx,
          cabys: l.cabys || l.sku || '',
          codigo: l.codigo || '',
          detalle: l.detalle || '',
          cantidad: l.cantidad || 1,
          precioUnitario: l.precioUnitario || 0,
          descuento: l.descuentoPorcentaje || l.descuento || 0,
          iva: l.impuestoTarifa != null ? l.impuestoTarifa : (l.iva != null ? l.iva : 13),
          impuestosAdicionales: Array.isArray(l.impuestosAdicionales) ? l.impuestosAdicionales : [],
        }));
        setLineas(cargadas);
        toast.success('Cotizacion cargada');
      } catch (e) {
        console.error(e);
        toast.error('Error al cargar cotizacion');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [paramId]);

  // ── Client search ───────────────────────────────────────────────────
  useEffect(() => {
    if (!clientSearchTerm || clientSearchTerm.length < 2) {
      setClientSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingClient(true);
      try {
        const res = await receptoresApi.list({ q: clientSearchTerm, limit: 20 });
        setClientSearchResults(res.data?.data || []);
      } catch { setClientSearchResults([]); }
      finally { setSearchingClient(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [clientSearchTerm]);

  // ── Product search with debounce ────────────────────────────────────
  useEffect(() => {
    if (!productSearchTerm || productSearchTerm.length < 2) {
      setProductSearchResults([]);
      setShowProductDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const res = await productosApi.list({ q: productSearchTerm });
        const results = res.data?.data || [];
        setProductSearchResults(results);
        setShowProductDropdown(results.length > 0);
        setHighlightedIndex(-1);
      } catch { setProductSearchResults([]); }
      finally { setSearchingProducts(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  // Close product dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (productInputRef.current && !productInputRef.current.contains(e.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Add product as line ─────────────────────────────────────────────
  const addProductLine = useCallback((product) => {
    const newLine = {
      id: Date.now() + Math.random(),
      cabys: product.codigoCabys || product.cabys || '',
      codigo: product.codigo || product.codigoComercial || '',
      detalle: product.descripcion || product.detalle || product.nombre || '',
      cantidad: 1,
      precioUnitario: product.precio || product.precioVenta || product.precioUnitario || 0,
      descuento: 0,
      iva: (() => {
        const tarifa = product.impuestoTarifa ?? product.iva ?? 13;
        if (!(receptor?.exoneracionActiva || receptor?.exoneracion_activa)) return tarifa;
        // Check if exoneration has specific CABYS - if so, only apply if product CABYS matches
        const exoCabys = receptor.exoneracionCabys || receptor.exoneracion_cabys;
        const productCabys = product.codigoCabys || product.cabys || '';
        if (exoCabys && Array.isArray(exoCabys) && exoCabys.length > 0 && productCabys) {
          const matches = exoCabys.some(c => productCabys.startsWith(c) || c.startsWith(productCabys));
          if (!matches) return tarifa; // CABYS not authorized for exoneration
        }
        return Math.max(0, tarifa - (receptor.exoneracionPorcentaje || receptor.exoneracion_porcentaje || 0));
      })(),
      // Impuestos adicionales del producto (911, Cruz Roja, etc.) — todo lo que no sea IVA ('01')
      impuestosAdicionales: (product.impuestos || []).filter(i => i.codigoImpuesto !== '01'),
    };
    setLineas(prev => [...prev, newLine]);
    setProductSearchTerm('');
    setShowProductDropdown(false);
    setProductSearchResults([]);
  }, [receptor]);

  // ── Add manual line ─────────────────────────────────────────────────
  const addManualLine = useCallback((text) => {
    setLineas(prev => [...prev, crearLineaVacia(text)]);
    setProductSearchTerm('');
    setShowProductDropdown(false);
  }, []);

  // ── Product input keyboard ──────────────────────────────────────────
  const handleProductKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.min(prev + 1, productSearchResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && productSearchResults[highlightedIndex]) {
        addProductLine(productSearchResults[highlightedIndex]);
      } else if (productSearchTerm.trim()) {
        addManualLine(productSearchTerm.trim());
      }
    } else if (e.key === 'Escape') {
      setShowProductDropdown(false);
    }
  };

  // ── Update line field ───────────────────────────────────────────────
  const updateLine = (lineId, field, value) => {
    setLineas(prev => prev.map(l => l.id === lineId ? { ...l, [field]: value } : l));
  };

  // ── Delete selected lines ──────────────────────────────────────────
  const deleteSelectedLines = () => {
    if (selectedLineIds.length === 0) return;
    setLineas(prev => prev.filter(l => !selectedLineIds.includes(l.id)));
    setSelectedLineIds([]);
  };

  // ── Toggle line selection ──────────────────────────────────────────
  const toggleLineSelect = (lineId) => {
    setSelectedLineIds(prev =>
      prev.includes(lineId) ? prev.filter(id => id !== lineId) : [...prev, lineId]
    );
  };

  const toggleAllLines = () => {
    if (selectedLineIds.length === lineas.length) {
      setSelectedLineIds([]);
    } else {
      setSelectedLineIds(lineas.map(l => l.id));
    }
  };

  // ── Totals ──────────────────────────────────────────────────────────
  const totals = lineas.reduce(
    (acc, l) => {
      const c = calcLinea(l);
      acc.subtotal += c.subtotal;
      acc.descuentos += c.descMonto;
      acc.impuestos += c.impuesto;
      acc.impAdicional += c.impAdicional;
      acc.total += c.total;
      return acc;
    },
    { subtotal: 0, descuentos: 0, impuestos: 0, impAdicional: 0, total: 0 }
  );

  // Desglose de impuestos adicionales (911, Cruz Roja, …) agrupados para mostrarlos
  const impuestosAdicionalesDesglose = (() => {
    const map = {};
    lineas.forEach(l => {
      const { base } = calcLinea(l);
      (l.impuestosAdicionales || []).forEach(imp => {
        const key = `${imp.codigoImpuesto}-${imp.codigo}`;
        if (!map[key]) {
          map[key] = { descripcion: imp.descripcion || (imp.codigoImpuesto === '07' ? 'Impuesto 911' : 'Cruz Roja'), monto: 0 };
        }
        map[key].monto += base * ((parseFloat(imp.porcentaje) || 0) / 100);
      });
    });
    return Object.values(map).filter(x => x.monto > 0);
  })();

  // ── Save ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (lineas.length === 0) {
      toast.error('Agregue al menos una linea');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        receptorId: receptor?.id || null,
        clienteNombre: receptor?.nombre || '',
        clienteEmail: receptor?.email || '',
        fecha: new Date().toISOString(),
        codigoMoneda,
        tipoCambio: Number(tipoCambio),
        fechaVencimiento: fechaVencimiento || null,
        notas,
        terminos,
        referenciaOportunidad,
        lineas: lineas.map(l => {
          const c = calcLinea(l);
          return {
            cabys: l.cabys,
            codigo: l.codigo,
            detalle: l.detalle,
            cantidad: Number(l.cantidad),
            precioUnitario: Number(l.precioUnitario),
            descuentoPorcentaje: Number(l.descuento),
            descuentoMonto: c.descMonto,
            impuestoTarifa: Number(l.iva),
            impuestoMonto: c.impuesto,
            impuestosAdicionales: l.impuestosAdicionales || [],
            subtotal: c.subtotal,
            total: c.total,
          };
        }),
      };

      let res;
      if (isEditing && cotizacionId) {
        res = await cotizacionesApi.update(cotizacionId, payload);
      } else {
        res = await cotizacionesApi.create(payload);
      }

      const saved = res.data?.data;
      if (saved?.id) setCotizacionId(saved.id);
      if (saved?.numero) setCotizacionNumero(saved.numero);

      toast.success(isEditing ? 'Cotizacion actualizada' : 'Cotizacion guardada');
      navigate('/facturacion/cotizaciones');
    } catch (e) {
      console.error(e);
      toast.error('Error al guardar cotizacion');
    } finally {
      setSaving(false);
    }
  };

  // ── Save and stay (for preview/email) ───────────────────────────────
  const handleSaveAndStay = async () => {
    if (lineas.length === 0) {
      toast.error('Agregue al menos una linea');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        receptorId: receptor?.id || null,
        clienteNombre: receptor?.nombre || '',
        clienteEmail: receptor?.email || '',
        fecha: new Date().toISOString(),
        codigoMoneda,
        tipoCambio: Number(tipoCambio),
        fechaVencimiento: fechaVencimiento || null,
        notas,
        terminos,
        referenciaOportunidad,
        lineas: lineas.map(l => {
          const c = calcLinea(l);
          return {
            cabys: l.cabys,
            codigo: l.codigo,
            detalle: l.detalle,
            cantidad: Number(l.cantidad),
            precioUnitario: Number(l.precioUnitario),
            descuentoPorcentaje: Number(l.descuento),
            descuentoMonto: c.descMonto,
            impuestoTarifa: Number(l.iva),
            impuestoMonto: c.impuesto,
            impuestosAdicionales: l.impuestosAdicionales || [],
            subtotal: c.subtotal,
            total: c.total,
          };
        }),
      };

      let res;
      if (cotizacionId) {
        res = await cotizacionesApi.update(cotizacionId, payload);
      } else {
        res = await cotizacionesApi.create(payload);
      }

      const saved = res.data?.data;
      if (saved?.id) setCotizacionId(saved.id);
      if (saved?.numero) setCotizacionNumero(saved.numero);

      toast.success('Cotizacion guardada');
      return saved;
    } catch (e) {
      console.error(e);
      toast.error('Error al guardar cotizacion');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ── Preview ─────────────────────────────────────────────────────────
  const handlePreview = () => {
    if (lineas.length === 0) {
      toast.error('Agregue al menos una linea para vista previa');
      return;
    }
    setShowPreview(true);
  };

  // ── Email ───────────────────────────────────────────────────────────
  const handleEmail = async () => {
    if (lineas.length === 0) {
      toast.error('Agregue al menos una linea');
      return;
    }
    if (!cotizacionId) {
      const saved = await handleSaveAndStay();
      if (!saved) return;
    }
    setShowEmailModal(true);
  };

  // ── Auto-expand textarea ────────────────────────────────────────────
  const autoExpand = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // ── ESC close modals ────────────────────────────────────────────────
  useEscClose(showClientModal ? () => setShowClientModal(false) : null);
  useEscClose(showEmailModal ? () => setShowEmailModal(false) : null);
  useEscClose(showPreview ? () => setShowPreview(false) : null);

  // ── Loading spinner ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        <span className="ml-3 text-gray-600">Cargando cotizacion...</span>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════
  return (
    <div className="px-2 sm:px-4 pb-8 overflow-x-auto">
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border p-3 mb-4 relative min-h-[70px]">
        <style>{`
          .toolbar-btn {
            min-width: clamp(45px, 5vw, 70px);
            padding: clamp(4px, 0.5vw, 8px) clamp(4px, 0.6vw, 12px);
          }
          .toolbar-icon {
            width: clamp(14px, 1.5vw, 20px);
            height: clamp(14px, 1.5vw, 20px);
          }
          .toolbar-label {
            font-size: clamp(8px, 0.9vw, 12px);
          }
        `}</style>
        <div className="flex items-center justify-center gap-2">
        <ToolbarButton icon={Save} label="Guardar" onClick={handleSave} disabled={saving} variant="warning" />
        <ToolbarButton icon={Eye} label="Vista Previa" onClick={handlePreview} variant="orange" />
        <ToolbarButton icon={Mail} label="Enviar Email" onClick={handleEmail} variant="success" />
        <ToolbarButton icon={X} label="Cancelar" onClick={() => navigate('/facturacion/cotizaciones')} />
        </div>
      </div>

      {/* ── Two-column layout ────────────────────────────────────────── */}
      {/* Top row: Client + Config + Notas | Lines + Totals */}
      <div className="flex gap-3 mb-3">
        <div className="w-72 flex-shrink-0 space-y-3">
          {/* Client card */}
          <div className="bg-white rounded-xl border-2 border-amber-200 p-4">
            <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
              <User className="h-4 w-4" /> Cliente
            </h3>
            {receptor ? (
              <div className="space-y-1">
                <p className="font-medium text-gray-900">{receptor.nombre}</p>
                {receptor.identificacion && (
                  <p className="text-sm text-gray-500">{receptor.identificacion}</p>
                )}
                {receptor.email && (
                  <p className="text-sm text-gray-500">{receptor.email}</p>
                )}
                {(receptor.exoneracionActiva || receptor.exoneracion_activa) && (
                  <div className="mt-1 flex items-center gap-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500"></span>
                    </span>
                    <span className="text-xs text-purple-600 font-medium">
                      Exonerado {receptor.exoneracionPorcentaje || receptor.exoneracion_porcentaje || 0}%
                    </span>
                  </div>
                )}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => setShowClientModal(true)}
                    className="text-xs text-amber-600 hover:text-amber-800 underline"
                  >
                    Cambiar
                  </button>
                  <button
                    onClick={() => setReceptor(null)}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    Quitar
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => { setShowClientModal(true); setClientSearchTerm(''); setClientSearchResults([]); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-amber-300 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors text-sm"
              >
                <UserPlus className="h-4 w-4" /> Asignar Cliente
              </button>
            )}
          </div>


          {/* Settings card */}
          <div className="bg-white rounded-xl border-2 border-amber-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-amber-700 mb-1 flex items-center gap-2">
              <DollarSign className="h-4 w-4" /> Configuracion
            </h3>

            {/* Moneda */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Moneda</label>
              <select
                value={codigoMoneda}
                onChange={(e) => setCodigoMoneda(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-amber-400 focus:border-amber-400"
              >
                {MONEDAS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            {/* Tipo cambio (if USD) */}
            {codigoMoneda === 'USD' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Cambio</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={tipoCambio}
                  onChange={(e) => setTipoCambio(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm [appearance:textfield] focus:ring-amber-400 focus:border-amber-400"
                />
              </div>
            )}

            {/* Fecha Vencimiento */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Fecha Vencimiento
              </label>
              <input
                type="date"
                value={fechaVencimiento}
                onChange={(e) => setFechaVencimiento(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-amber-400 focus:border-amber-400"
              />
            </div>

            {/* Referencia de Oportunidad */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Referencia de Oportunidad</label>
              <input
                type="text"
                value={referenciaOportunidad}
                onChange={(e) => setReferenciaOportunidad(e.target.value)}
                placeholder="Ej: OPP-00123"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
          </div>


          {/* Notes card */}
          <div className="bg-white rounded-xl border-2 border-amber-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-amber-700 mb-1 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Notas
            </h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones</label>
              <textarea
                value={notas}
                onChange={(e) => { setNotas(e.target.value); autoExpand(e); }}
                rows={2}
                placeholder="Observaciones internas o para el cliente..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Terminos y Condiciones</label>
              <textarea
                value={terminos}
                onChange={(e) => { setTerminos(e.target.value); autoExpand(e); }}
                rows={2}
                placeholder="Terminos y condiciones de la cotizacion..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-amber-400 focus:border-amber-400"
              />
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          {/* Product search */}
          <div className="relative" ref={productInputRef}>
            <div className="flex items-center bg-white border-2 border-amber-200 rounded-xl px-3 py-2">
              <Search className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
              <input
                type="text"
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                onKeyDown={handleProductKeyDown}
                onFocus={() => { if (productSearchResults.length > 0) setShowProductDropdown(true); }}
                placeholder="Buscar producto o escribir descripcion + Enter..."
                className="flex-1 outline-none text-sm"
              />
              {searchingProducts && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
            </div>

            {/* Product dropdown */}
            {showProductDropdown && productSearchResults.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                {productSearchResults.map((p, idx) => (
                  <button
                    key={p.id || idx}
                    onClick={() => addProductLine(p)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-amber-50 border-b border-gray-100 last:border-0 flex items-center justify-between
                      ${highlightedIndex === idx ? 'bg-amber-50' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-gray-900 truncate block">
                        {p.descripcion || p.detalle || p.nombre}
                      </span>
                      <span className="text-xs text-gray-500">
                        {p.codigo || ''} {(p.codigoCabys || p.cabys) ? `| CABYS: ${p.codigoCabys || p.cabys}` : ''} {p.precio > 0 ? '' : ''}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-amber-600 ml-3 whitespace-nowrap">
                      {fmt(p.precio || p.precioVenta || p.precioUnitario || 0, codigoMoneda)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Lines table */}
          <div className="bg-white rounded-xl border-2 border-amber-200 overflow-hidden">
            {/* Table header actions */}
            {selectedLineIds.length > 0 && (
              <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
                <span className="text-sm text-red-600">{selectedLineIds.length} linea(s) seleccionada(s)</span>
                <button
                  onClick={deleteSelectedLines}
                  className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800"
                >
                  <Trash2 className="h-4 w-4" /> Eliminar
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-50 border-b border-amber-200">
                    <th className="px-2 py-2 text-center w-8">
                      <input
                        type="checkbox"
                        checked={lineas.length > 0 && selectedLineIds.length === lineas.length}
                        onChange={toggleAllLines}
                        className="rounded border-gray-300 text-amber-600 focus:ring-amber-400"
                      />
                    </th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-amber-700">CABYS</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-amber-700">Codigo</th>
                    <th className="px-2 py-2 text-left text-xs font-semibold text-amber-700 min-w-[200px]">Descripcion</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-amber-700 w-20">Cant</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-amber-700 w-28">Precio</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-amber-700 w-20">Desc%</th>
                    <th className="px-2 py-2 text-center text-xs font-semibold text-amber-700 w-28">IVA</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-amber-700 w-24">Impuesto</th>
                    <th className="px-2 py-2 text-right text-xs font-semibold text-amber-700 w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center py-12 text-gray-400">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p>No hay lineas. Busque un producto arriba o presione Enter para agregar una linea manual.</p>
                      </td>
                    </tr>
                  ) : (
                    lineas.map((linea) => {
                      const c = calcLinea(linea);
                      return (
                        <tr key={linea.id} className="border-b border-gray-100 hover:bg-amber-50/30">
                          <td className="px-2 py-1 text-center">
                            <input
                              type="checkbox"
                              checked={selectedLineIds.includes(linea.id)}
                              onChange={() => toggleLineSelect(linea.id)}
                              className="rounded border-gray-300 text-amber-600 focus:ring-amber-400"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              value={linea.cabys}
                              onChange={(e) => updateLine(linea.id, 'cabys', e.target.value)}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:ring-amber-400 focus:border-amber-400"
                              placeholder="CABYS"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              value={linea.codigo}
                              onChange={(e) => updateLine(linea.id, 'codigo', e.target.value)}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:ring-amber-400 focus:border-amber-400"
                              placeholder="Cod"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <textarea
                              value={linea.detalle}
                              onChange={(e) => { updateLine(linea.id, 'detalle', e.target.value); autoExpand(e); }}
                              rows={1}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs resize-none overflow-hidden focus:ring-amber-400 focus:border-amber-400"
                              placeholder="Descripcion del producto o servicio"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={linea.cantidad}
                              onChange={(e) => updateLine(linea.id, 'cantidad', e.target.value)}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right [appearance:textfield] focus:ring-amber-400 focus:border-amber-400"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={linea.precioUnitario}
                              onChange={(e) => updateLine(linea.id, 'precioUnitario', e.target.value)}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right [appearance:textfield] focus:ring-amber-400 focus:border-amber-400"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={linea.descuento}
                              onChange={(e) => updateLine(linea.id, 'descuento', e.target.value)}
                              className="w-full border border-gray-200 rounded px-2 py-1 text-xs text-right [appearance:textfield] focus:ring-amber-400 focus:border-amber-400"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <select
                              value={linea.iva}
                              onChange={(e) => updateLine(linea.id, 'iva', Number(e.target.value))}
                              className="w-full border border-gray-200 rounded px-1 py-1 text-xs focus:ring-amber-400 focus:border-amber-400"
                            >
                              {IVA_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1 text-right text-xs text-gray-600 whitespace-nowrap">
                            {fmt(c.impuesto, codigoMoneda)}
                          </td>
                          <td className="px-2 py-1 text-right text-xs font-medium text-gray-900 whitespace-nowrap">
                            {fmt(c.total, codigoMoneda)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Add line button */}
            <div className="px-4 py-2 border-t border-gray-100">
              <button
                onClick={() => setLineas(prev => [...prev, crearLineaVacia()])}
                className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-800"
              >
                <Plus className="h-4 w-4" /> Agregar linea
              </button>
            </div>
          </div>

          {/* Totals panel */}
          <div className="bg-white rounded-xl border-2 border-amber-200 p-4">
            <div className="flex justify-end">
              <div className="w-full sm:w-72 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{fmt(totals.subtotal, codigoMoneda)}</span>
                </div>
                {totals.descuentos > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Descuentos</span>
                    <span className="font-medium text-red-500">-{fmt(totals.descuentos, codigoMoneda)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Impuesto (IVA)</span>
                  <span className="font-medium">{fmt(totals.impuestos, codigoMoneda)}</span>
                </div>
                {impuestosAdicionalesDesglose.map((imp, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-600">{imp.descripcion}</span>
                    <span className="font-medium">{fmt(imp.monto, codigoMoneda)}</span>
                  </div>
                ))}
                <div className="border-t border-amber-200 pt-2 flex justify-between text-base">
                  <span className="font-semibold text-amber-700">Total</span>
                  <span className="font-bold text-amber-700">{fmt(totals.total, codigoMoneda)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* ══════════════════════════════════════════════════════════════ */}
      {/* MODALS                                                        */}
      {/* ══════════════════════════════════════════════════════════════ */}

      {/* ── Client search modal ──────────────────────────────────────── */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowClientModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Buscar Cliente</h3>
              <button onClick={() => setShowClientModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-3">
              <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2">
                <Search className="h-4 w-4 text-gray-400 mr-2" />
                <input
                  type="text"
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, identificacion o email..."
                  className="flex-1 outline-none text-sm"
                  autoFocus
                />
                {searchingClient && <Loader2 className="h-4 w-4 animate-spin text-amber-500" />}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-4">
              {clientSearchResults.length === 0 && clientSearchTerm.length >= 2 && !searchingClient && (
                <p className="text-center text-sm text-gray-400 py-8">No se encontraron clientes</p>
              )}
              {clientSearchResults.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-xs font-semibold text-gray-500">Nombre</th>
                      <th className="text-left py-2 text-xs font-semibold text-gray-500">Identificacion</th>
                      <th className="text-left py-2 text-xs font-semibold text-gray-500">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientSearchResults.map(c => (
                      <tr
                        key={c.id}
                        onClick={() => {
                          setReceptor(c);
                          setShowClientModal(false);
                        }}
                        className="border-b border-gray-100 hover:bg-amber-50 cursor-pointer"
                      >
                        <td className="py-2 font-medium text-gray-900">{c.nombre}</td>
                        <td className="py-2 text-gray-600">{c.identificacion || '-'}</td>
                        <td className="py-2 text-gray-600">{c.email || c.emailFacturacion || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Email modal (EnviarCotizacionModal) ──────────────────────── */}
      {showEmailModal && (
        <EnviarCotizacionModal
          cotizacionId={cotizacionId}
          numero={cotizacionNumero}
          clienteNombre={receptor?.nombre || ''}
          clienteEmail={receptor?.email || ''}
          total={fmt(totals.total, codigoMoneda)}
          emisorNombre={emisorData?.nombre || currentEmisor?.nombre || ''}
          emisorColor={emisorData?.template_color || currentEmisor?.template_color || '#F47920'}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* ── Preview modal ────────────────────────────────────────────── */}
      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-amber-50">
              <h2 className="text-lg font-semibold text-gray-900">
                Vista Previa - Cotización {cotizacionNumero || ''}
              </h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] bg-gray-100">
              <InvoiceTemplate
                emisor={{...(emisorData || currentEmisor), terminos_condiciones: terminos || ''}}
                receptor={receptor ? {
                  nombre: receptor.nombre,
                  identificacion: receptor.identificacion,
                  tipo_identificacion: receptor.tipoIdentificacion || receptor.tipo_identificacion,
                  email: receptor.email,
                  email_facturacion: receptor.emailFacturacion || receptor.email_facturacion || receptor.email,
                  telefono_codigo: receptor.telefonoCodigo || receptor.telefono_codigo || '506',
                  telefono_numero: receptor.telefonoNumero || receptor.telefono_numero,
                  otras_senas: receptor.otrasSenas || receptor.otras_senas,
                  provincia: receptor.provincia,
                  canton: receptor.canton,
                  distrito: receptor.distrito,
                } : null}
                documento={{
                  consecutivo: cotizacionNumero || 'COT-BORRADOR',
                  fechaEmision: new Date().toISOString(),
                  codigoMoneda,
                  notas,
                }}
                lineas={lineas.map((l, idx) => {
                  const c = calcLinea(l);
                  return {
                    numeroLinea: idx + 1,
                    codigo: l.cabys,
                    codigoComercial: l.codigo,
                    detalle: l.detalle,
                    cantidad: Number(l.cantidad),
                    precioUnitario: Number(l.precioUnitario),
                    descuento: c.descMonto,
                    impuestoMonto: c.impuesto,
                    impuestoTarifa: Number(l.iva),
                    impuestosAdicionales: l.impuestosAdicionales || [],
                    montoTotalLinea: c.total,
                    impuestoExoneracion: (() => {
                      if (!receptor?.exoneracionActiva) return null;
                      const exos = receptor.exoneraciones || [];
                      const cabys = l.cabys || '';
                      for (const exo of exos) {
                        const exoCabys = exo.cabys;
                        if (!exoCabys || !Array.isArray(exoCabys) || exoCabys.length === 0 ||
                            exoCabys.some(ec => cabys === ec)) {
                          return { porcentaje: exo.porcentaje, monto: c.subtotal * exo.porcentaje / 100, numeroDocumento: exo.numeroDocumento, tipoDocumentoDesc: exo.tipoDocumentoDesc || 'Exoneración' };
                        }
                      }
                      // Legacy fallback
                      if (receptor.exoneracionPorcentaje && Number(l.iva) === 0) {
                        return { porcentaje: receptor.exoneracionPorcentaje, monto: c.subtotal * receptor.exoneracionPorcentaje / 100, numeroDocumento: receptor.exoneracionNumero, tipoDocumentoDesc: receptor.exoneracionTipoDocDesc || 'Exoneración' };
                      }
                      return null;
                    })(),
                  };
                })}
                totales={{
                  totalVentaNeta: totals.subtotal,
                  totalImpuesto: totals.impuestos,
                  totalComprobante: totals.total,
                }}
                esCotizacion={true}
                tipoCambio={Number(tipoCambio) || 1}
                fullWidth={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// EnviarCotizacionModal
// ══════════════════════════════════════════════════════════════════════
function EnviarCotizacionModal({ cotizacionId, numero, clienteNombre, clienteEmail, total, emisorNombre, emisorColor = '#F47920', onClose }) {
  const [to, setTo] = useState(clienteEmail);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [verCc, setVerCc] = useState(false);
  const [verBcc, setVerBcc] = useState(false);
  const [subject, setSubject] = useState(`Cotización ${numero || ''} - ${emisorNombre}`);
  const [mensaje, setMensaje] = useState(
    `Estimado/a ${clienteNombre || 'cliente'},\n\nEs un placer ponernos en contacto con usted. Adjunto encontrará nuestra cotización ${numero || ''} por un monto de ${total}.\n\nQuedamos a su disposición para cualquier consulta o aclaración.\n\nSaludos cordiales,\n${emisorNombre}`
  );
  const [sending, setSending] = useState(false);

  useEscClose(onClose);

  // Vista previa real del correo (lo arma el servidor con el mismo diseño del envío)
  const [previewHtml, setPreviewHtml] = useState('');
  useEffect(() => {
    let cancelado = false;
    const t = setTimeout(async () => {
      try {
        const r = await cotizacionesApi.previewEmail(cotizacionId, { message: mensaje });
        if (!cancelado) setPreviewHtml(r.data?.html || '');
      } catch {
        if (!cancelado) setPreviewHtml('<p style="font-family:sans-serif;color:#94a3b8;padding:24px;">No se pudo cargar la vista previa.</p>');
      }
    }, 400);
    return () => { cancelado = true; clearTimeout(t); };
  }, [cotizacionId, mensaje]);

  const handleSend = async () => {
    if (!to) {
      toast.error('Ingrese un destinatario');
      return;
    }
    setSending(true);
    try {
      const r = await cotizacionesApi.sendEmail(cotizacionId, { to, cc, bcc, subject, message: mensaje });
      toast.success(r.data?.message || 'Cotización enviada por email');
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Error al enviar email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-slate-700 to-slate-800 rounded-t-xl">
          <h3 className="font-semibold text-white flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5" /> Enviar Cotización
          </h3>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Email fields */}
          <div className="px-6 pt-5 space-y-3">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-500 w-14 text-right">Para:</label>
              <input
                type="text"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="email@ejemplo.com (varios separados por coma)"
                className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                autoFocus
              />
              <div className="flex gap-2 text-sm">
                {!verCc && <button type="button" onClick={() => setVerCc(true)} className="text-gray-500 hover:text-gray-900 hover:underline">CC</button>}
                {!verBcc && <button type="button" onClick={() => setVerBcc(true)} className="text-gray-500 hover:text-gray-900 hover:underline">CCO</button>}
              </div>
            </div>
            {verCc && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-500 w-14 text-right">CC:</label>
                <input
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="Con copia (varios separados por coma)"
                  className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  autoFocus
                />
                <button type="button" onClick={() => { setVerCc(false); setCc(''); }} className="text-gray-400 hover:text-gray-600" title="Quitar CC"><X className="h-4 w-4" /></button>
              </div>
            )}
            {verBcc && (
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-500 w-14 text-right" title="Copia oculta: los demás destinatarios no la ven">CCO:</label>
                <input
                  type="text"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  placeholder="Copia oculta (varios separados por coma)"
                  className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                  autoFocus={!verCc}
                />
                <button type="button" onClick={() => { setVerBcc(false); setBcc(''); }} className="text-gray-400 hover:text-gray-600" title="Quitar CCO"><X className="h-4 w-4" /></button>
              </div>
            )}
            <p className="pl-[4.25rem] text-xs text-gray-500">
              En Para, CC y CCO puede poner varios correos separados por coma. Los de CCO no los ven los demás destinatarios.
            </p>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-500 w-14 text-right">Asunto:</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="flex-1 border-b border-gray-200 px-2 py-2 text-sm focus:outline-none focus:border-amber-400 transition-colors"
              />
            </div>
          </div>

          {/* Message */}
          <div className="px-6 pt-4">
            <label className="block text-sm font-medium text-gray-500 mb-2">Mensaje:</label>
            <textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={7}
              className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400 transition-all resize-none leading-relaxed"
            />
          </div>

          {/* Preview */}
          <div className="px-6 py-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Vista previa del correo</p>
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
              <iframe title="Vista previa del correo" srcDoc={previewHtml} className="w-full h-[640px] border-0 bg-[#EFF1F7]" />
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <FileText className="h-3.5 w-3.5" />
              <span>Se adjuntará automáticamente: <strong className="text-gray-600">{numero || 'Cotización'}.pdf</strong></span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-6 py-2.5 border-2 border-amber-500 text-amber-600 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-amber-50 transition-colors"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Enviar Cotización
          </button>
        </div>
      </div>
    </div>
  );
}
